/**
 * MortgageInputForm
 *
 * Form component for mortgage calculator inputs.
 * Handles currency formatting, sliders, and dropdowns.
 */

import { useCallback, useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import type { MortgageInputs, LoanTerm, OtherExpense } from '@/types/mortgage';
import { LOAN_TERM_OPTIONS, MAX_OTHER_EXPENSES } from '@/types/mortgage';
import { formatPrice } from '@/utils';

// Standard width for every input on the right side of the form so labels and
// values line up cleanly on every row.
const INPUT_WIDTH = 'w-40';

interface MortgageInputFormProps {
    inputs: MortgageInputs;
    loanAmount: number;
    onUpdateInput: <K extends keyof MortgageInputs>(key: K, value: MortgageInputs[K]) => void;
    onUpdatePurchasePrice: (price: number) => void;
    onUpdateDownPaymentByPercentage: (percentage: number) => void;
    onAddOtherExpense: (expense?: Partial<Omit<OtherExpense, 'id'>>) => void;
    onUpdateOtherExpense: (id: string, patch: Partial<Omit<OtherExpense, 'id'>>) => void;
    onRemoveOtherExpense: (id: string) => void;
}

export function MortgageInputForm({
    inputs,
    loanAmount,
    onUpdateInput,
    onUpdatePurchasePrice,
    onUpdateDownPaymentByPercentage,
    onAddOtherExpense,
    onUpdateOtherExpense,
    onRemoveOtherExpense,
}: MortgageInputFormProps) {
    return (
        <>
            <div className="space-y-4">
                {/* Purchase Price */}
                <CurrencyInput
                    label="Purchase Price"
                    value={inputs.purchasePrice}
                    onChange={onUpdatePurchasePrice}
                />

                {/* Down Payment Display */}
                <FormRow label="Down Payment">
                    <div className={`${INPUT_WIDTH} flex items-center justify-end gap-2 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm`}>
                        <span>{formatPrice(inputs.downPayment)}</span>
                        <span className="text-xs text-gray-400">|</span>
                        <span>{inputs.downPaymentPercentage.toFixed(1)}%</span>
                    </div>
                </FormRow>

                {/* Down Payment Slider */}
                <Slider
                    value={inputs.downPaymentPercentage}
                    onChange={onUpdateDownPaymentByPercentage}
                    min={0}
                    max={100}
                    step={0.5}
                />

                {/* Loan Amount (Read-only) */}
                <FormRow label="Loan Amount">
                    <div className={`${INPUT_WIDTH} px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-sm text-right`}>
                        {formatPrice(loanAmount)}
                    </div>
                </FormRow>

                {/* Loan Term */}
                <FormRow label="Loan Term">
                    <select
                        value={inputs.loanTerm}
                        onChange={(e) => onUpdateInput('loanTerm', parseInt(e.target.value) as LoanTerm)}
                        className={`${INPUT_WIDTH} px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-primary focus:border-transparent`}
                    >
                        {LOAN_TERM_OPTIONS.map((term) => (
                            <option key={term} value={term}>
                                {term} years
                            </option>
                        ))}
                    </select>
                </FormRow>

                {/* Interest Rate */}
                <PercentageInput
                    label="Interest Rate"
                    value={inputs.interestRate}
                    onChange={(value) => onUpdateInput('interestRate', value)}
                    min={0}
                    max={20}
                    step={0.01}
                />

                {/* Annual Property Tax */}
                <CurrencyInput
                    label="Annual Property Tax"
                    value={inputs.annualPropertyTax}
                    onChange={(value) => onUpdateInput('annualPropertyTax', value)}
                />

                {/* PMI — caveat note now lives in the disclosure block on the parent page */}
                <PercentageInput
                    label="PMI"
                    value={inputs.pmiPercentage}
                    onChange={(value) => onUpdateInput('pmiPercentage', value)}
                    min={0}
                    max={5}
                    step={0.1}
                />

                {/* Annual Home Insurance */}
                <CurrencyInput
                    label="Annual Home Insurance"
                    value={inputs.annualHomeInsurance}
                    onChange={(value) => onUpdateInput('annualHomeInsurance', value)}
                    allowNA
                />

                {/* Monthly HOA */}
                <CurrencyInput
                    label="Monthly HOA"
                    value={inputs.monthlyHOA}
                    onChange={(value) => onUpdateInput('monthlyHOA', value)}
                    allowNA
                />

                {/* Other Monthly Expenses — custom list */}
                <OtherExpensesList
                    expenses={inputs.otherExpenses}
                    onAdd={onAddOtherExpense}
                    onUpdate={onUpdateOtherExpense}
                    onRemove={onRemoveOtherExpense}
                />
            </div>
        </>
    );
}

// ============================================================================
// Other Expenses (custom subscriptions / car payment / etc.)
// ============================================================================

interface OtherExpensesListProps {
    expenses: OtherExpense[];
    onAdd: (expense?: Partial<Omit<OtherExpense, 'id'>>) => void;
    onUpdate: (id: string, patch: Partial<Omit<OtherExpense, 'id'>>) => void;
    onRemove: (id: string) => void;
}

function OtherExpensesList({ expenses, onAdd, onUpdate, onRemove }: OtherExpensesListProps) {
    const atLimit = expenses.length >= MAX_OTHER_EXPENSES;
    return (
        <div className="space-y-2 pt-2 border-t border-gray-100">
            <div className="flex items-baseline justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-700">Other Monthly Expenses</p>
                    <p className="text-[11px] text-gray-400">
                        Add subscriptions, car payment, daycare — anything that should roll into your monthly total.
                    </p>
                </div>
            </div>

            {expenses.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No custom expenses yet.</p>
            ) : (
                <div className="space-y-2">
                    {expenses.map((expense) => (
                        <OtherExpenseRow
                            key={expense.id}
                            expense={expense}
                            onUpdate={(patch) => onUpdate(expense.id, patch)}
                            onRemove={() => onRemove(expense.id)}
                        />
                    ))}
                </div>
            )}

            <button
                type="button"
                onClick={() => onAdd()}
                disabled={atLimit}
                className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:text-primary/80 disabled:text-gray-300 disabled:cursor-not-allowed"
                title={atLimit ? `Maximum ${MAX_OTHER_EXPENSES} expenses` : 'Add another monthly expense'}
            >
                <Plus size={14} />
                Add expense
            </button>
        </div>
    );
}

interface OtherExpenseRowProps {
    expense: OtherExpense;
    onUpdate: (patch: Partial<Omit<OtherExpense, 'id'>>) => void;
    onRemove: () => void;
}

function OtherExpenseRow({ expense, onUpdate, onRemove }: OtherExpenseRowProps) {
    const handleAmountChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value.replace(/[^0-9]/g, '');
            onUpdate({ amount: parseInt(raw, 10) || 0 });
        },
        [onUpdate],
    );

    const displayAmount = expense.amount === 0 ? '' : formatPrice(expense.amount);

    return (
        <div className="flex items-center gap-2">
            <input
                type="text"
                value={expense.label}
                onChange={(e) => onUpdate({ label: e.target.value })}
                placeholder="e.g. Netflix"
                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-gray-400"
            />
            <div className={`${INPUT_WIDTH} relative shrink-0`}>
                <input
                    type="text"
                    inputMode="numeric"
                    value={displayAmount}
                    onChange={handleAmountChange}
                    placeholder="$0"
                    className="w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md text-sm text-right focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-gray-400"
                />
            </div>
            <button
                type="button"
                onClick={onRemove}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                aria-label={`Remove ${expense.label || 'expense'}`}
                title="Remove"
            >
                <Trash2 size={16} />
            </button>
        </div>
    );
}

// ============================================================================
// Reusable Sub-components
// ============================================================================

interface FormRowProps {
    label: string;
    children: React.ReactNode;
}

function FormRow({ label, children }: FormRowProps) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            {children}
        </div>
    );
}

interface SliderProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
}

function Slider({ value, onChange, min = 0, max = 100, step = 1 }: SliderProps) {
    return (
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
        />
    );
}

interface CurrencyInputProps {
    label: string;
    value: number;
    onChange: (value: number) => void;
    allowNA?: boolean;
    hideLabel?: boolean;
}

function CurrencyInput({ label, value, onChange, allowNA = false, hideLabel = false }: CurrencyInputProps) {
    const [isFocused, setIsFocused] = useState(false);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const rawValue = e.target.value.replace(/[^0-9]/g, '');
            onChange(parseInt(rawValue) || 0);
        },
        [onChange]
    );

    const handleClear = useCallback(() => {
        onChange(0);
    }, [onChange]);

    // Show empty field when value is 0 and allowNA is true
    const displayValue = allowNA && value === 0 ? '' : formatPrice(value);
    // Show N/A placeholder only when not focused and value is 0
    const placeholder = allowNA && !isFocused ? 'N/A' : undefined;

    const handleFocus = useCallback(() => setIsFocused(true), []);
    const handleBlur = useCallback(() => setIsFocused(false), []);

    const inputElement = (
        <div className={`relative ${INPUT_WIDTH}`}>
            <input
                type="text"
                inputMode="numeric"
                value={displayValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onTouchStart={handleFocus}
                placeholder={placeholder}
                className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-md text-sm text-right focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-gray-400"
            />
            {value > 0 && (
                <button
                    type="button"
                    onClick={handleClear}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                    <X size={16} />
                </button>
            )}
        </div>
    );

    if (hideLabel) {
        return inputElement;
    }

    return <FormRow label={label}>{inputElement}</FormRow>;
}

interface PercentageInputProps {
    label: string;
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
}

function PercentageInput({
    label,
    value,
    onChange,
    min = 0,
    max = 100,
    step = 0.01,
}: PercentageInputProps) {
    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const rawValue = e.target.value.replace(/[^0-9.]/g, '');
            const numValue = parseFloat(rawValue) || 0;
            onChange(Math.min(Math.max(numValue, min), max));
        },
        [onChange, min, max]
    );

    return (
        <div className="space-y-2">
            <FormRow label={label}>
                <div className={`${INPUT_WIDTH} relative`}>
                    <input
                        type="text"
                        value={value.toFixed(2)}
                        onChange={handleInputChange}
                        className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-md text-sm text-right focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                        %
                    </span>
                </div>
            </FormRow>
            <Slider value={value} onChange={onChange} min={min} max={max} step={step} />
        </div>
    );
}
